import React, { useState, useEffect } from "react";
import { Download, Terminal } from "lucide-react";
import windowsLogo from "../images/windows-logo.webp";
import macLogo from "../images/mac-logo.webp";
import linuxLogo from "../images/linux-logo.png";
import appLogo from "../images/app-logo.png";

const REPO_OWNER = "aaronfisher-code";
const REPO_NAME = "Alpha-Income-Management";

const DownloadPage = () => {
  const [releaseData, setReleaseData] = useState(null);
  const [userOS, setUserOS] = useState("");

  useEffect(() => {
    const detectOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      if (userAgent.indexOf("win") > -1) return "windows";
      if (userAgent.indexOf("mac") > -1) return "mac";
      if (userAgent.indexOf("linux") > -1) return "linux";
      return "unknown";
    };

    setUserOS(detectOS());

    const fetchReleaseData = async () => {
      try {
        const response = await fetch(
          `https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/releases/latest`
        );
        const data = await response.json();
        setReleaseData(data);
      } catch (error) {
        console.error("Error fetching release data:", error);
      }
    };

    fetchReleaseData();
  }, []);

  if (!releaseData) {
    return <div className="text-center p-8">Loading...</div>;
  }

  const getDownloadLink = (os) => {
    const osMap = {
      windows: "win-x64",
      mac: "mac",
      linux: "linux-x64",
    };
    const asset = releaseData.assets.find((asset) =>
      asset.name.includes(osMap[os])
    );
    return asset ? asset.browser_download_url : "#";
  };

  const getOSName = (os) => {
    const osNames = {
      windows: "Windows (x64)",
      mac: "Mac",
      linux: "Linux (x64)",
    };
    return osNames[os] || "Unknown OS";
  };

  const getOSLogo = (os) => {
    const logos = {
      windows: windowsLogo,
      mac: macLogo,
      linux: linuxLogo,
    };
    return logos[os] || "/api/placeholder/32/32?text=Unknown";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const recommendedDownload = getDownloadLink(userOS);

  return (
    <div className="min-h-screen bg-gray-900 text-white font-inter">
      <div className="max-w-6xl mx-auto p-6">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
          <div className="flex items-center mb-4 md:mb-0">
            <img
              src={appLogo}
              alt="Alpha Income Logo"
              className="w-12 h-12 mr-4"
            />
            <div>
              <h1 className="text-4xl font-bold tracking-tight">
                Download Alpha Income
              </h1>
              <p className="text-gray-400 mt-1">
                An all in one solution for managing pharmacy income
              </p>
            </div>
          </div>
          <div className="text-left md:text-right">
            <p className="text-2xl font-semibold">
              Version {releaseData.tag_name}
            </p>
            <p className="text-gray-400">
              {formatDate(releaseData.published_at)}
            </p>
          </div>
        </header>

        <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8">
          <h2 className="text-2xl font-bold mb-4">
            Recommended Download for Your System
          </h2>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center mb-4 sm:mb-0">
              <img
                src={getOSLogo(userOS)}
                alt={`${getOSName(userOS)} logo`}
                className="w-8 h-8 mr-3"
              />
              <span className="text-xl">{getOSName(userOS)}</span>
            </div>
            <a
              href={recommendedDownload}
              className="flex items-center justify-center bg-blue-500 text-white py-2 px-6 rounded-full hover:bg-blue-600 transition duration-300 text-lg font-medium"
            >
              <Download className="mr-2" size={24} />
              Download
            </a>
          </div>
        </div>

        <h2 className="text-3xl font-bold mb-6">All Downloads</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {["windows", "mac", "linux"].map((os) => (
            <div
              key={os}
              className={`bg-gray-800 p-5 rounded-lg shadow-md ${
                os === userOS ? "ring-2 ring-blue-500" : ""
              }`}
            >
              <div className="flex items-center mb-4">
                <img
                  src={getOSLogo(os)}
                  alt={`${getOSName(os)} logo`}
                  className="w-8 h-8 mr-3"
                />
                <h3 className="text-xl font-semibold">{getOSName(os)}</h3>
              </div>
              <a
                href={getDownloadLink(os)}
                className="flex items-center justify-center bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-300 w-full text-lg font-medium"
              >
                <Download className="mr-2" size={20} />
                Download
              </a>
            </div>
          ))}
        </div>

        <div className="mt-8 mb-12 bg-gray-800 p-4 rounded-lg">
          <div className="flex items-center mb-2">
            <Terminal size={20} className="mr-2 text-gray-400" />
            <h3 className="text-lg font-medium">Command-line Installation</h3>
          </div>
          <p className="text-sm text-gray-400 mb-2">
            Launch app installer via command-line on Linux, Mac, or Windows:
          </p>
          <div className="bg-gray-900 p-3 rounded overflow-x-auto mb-2">
            <code className="text-sm font-mono text-gray-300">
              /bin/bash -c "$(curl -fsSL
              https://www.jdeploy.com/gh/aaronfisher-code/Alpha-Income-Management/
              {releaseData.tag_name}/install.sh)"
            </code>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(
                `/bin/bash -c "$(curl -fsSL https://www.jdeploy.com/gh/aaronfisher-code/Alpha-Income-Management/${releaseData.tag_name}/install.sh)"`
              );
            }}
            className="bg-gray-700 hover:bg-gray-600 py-1 px-3 rounded text-sm transition duration-300"
          >
            Copy to clipboard
          </button>
        </div>

        <footer className="text-center text-sm text-gray-400">
          <p>Download page powered by GitHub Releases</p>
          <p className="mt-1">
            Alpha Income Management - Simplifying pharmacy income management
          </p>
        </footer>
      </div>
    </div>
  );
};

export default DownloadPage;
